import { useThemeStore } from '@/store/themeStore';
import { PATIENT_CONSUMER_PROFESSION_ID } from '@/types';
import { generateProfessionsSelectOptions } from '@/utils/forms';
import { SelectFieldData } from '@/components/forms/inputs/SelectField/SelectField';
import { usePhrase } from '../../general/usePhrase/usePhrase';
import { omitBy } from '@/utils/fp/object';
import {
    IAllRegistrationData,
    IWalletPersonalData,
    DefaultPageProps,
    IAccountProfessionalDetails,
    IAllAccountData,
} from '@/types/types';

export interface UseProfessionalDetailsProps {
    dictionary: DefaultPageProps['dictionary'];
    registrationData?: IAllRegistrationData;
    accountData?: IAllAccountData;
    nonMemberPersonalData?: Partial<IWalletPersonalData>;
    restrictCountries?: boolean;
}

export const useProfessionalDetails = (props: UseProfessionalDetailsProps) => {
    const { t } = usePhrase();
    const { variables } = useThemeStore();

    const { registrationData, accountData, nonMemberPersonalData, dictionary } = props;
    const { specialties, professions: professionsData } = dictionary;

    const countries = props.restrictCountries
        ? dictionary?.countries?.filter((item) => item.isRegistrationOpen)
        : dictionary?.countries;

    const generateMedicalNumberConstraints = () => {
        const currentCountry = countries?.find(
            (c) =>
                c.countryCode === registrationData?.countryCode ||
                c.countryCode === nonMemberPersonalData?.countryCode,
        );
        if (currentCountry) {
            const {
                medicalNumberConstraint,
                medicalNumberPhraseId,
                medicalNumberErrorPhraseId,
                medicalNumberOptOutPhraseId,
            } = currentCountry;
            return {
                medicalNumberConstraint,
                medicalNumberPhraseId,
                medicalNumberErrorPhraseId,
                medicalNumberOptOutPhraseId,
            };
        }
        return undefined;
    };

    const generateSpecialtiesList = (): SelectFieldData<
        NonNullable<IAccountProfessionalDetails['specialtyId']>
    >[] =>
        specialties
            ?.filter((item) => item.phraseId && item.specialtyId)
            .map((item) => ({
                label: item.phraseId,
                value: item.specialtyId,
            })) ?? [];

    const generateYearsList = (): SelectFieldData[] => {
        const years = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);
        return years.map((item) => ({
            label: item.toString(),
            useRawLabel: true,
            value: item.toString(),
        }));
    };

    const professionsDataWithoutPatientConsumer = professionsData.filter(
        (x) => x.professionId !== PATIENT_CONSUMER_PROFESSION_ID,
    );

    const getDefaultValues = async () => {
        const values = omitBy(registrationData, (val) => val === '');
        return Promise.resolve({
            specialtyId: values?.specialtyId,
            medicalNumber: values?.medicalNumber,
            medicalNumberNPI: values?.medicalNumberNPI,
            medicalNumberOptOut: values?.medicalNumberOptOut,
            graduationYear: values?.graduationYear,
            medicalSchool: values?.medicalSchool,
            stateCodeLicensed: values?.stateCodeLicensed,
            primaryWorkplace: values?.primaryWorkplace,
            primaryWorkplacePhone: values?.primaryWorkplacePhone,
            professionalIdNumber: values?.professionalIdNumber,
            professionalTitle: values?.professionalTitle,
            jobTitle: values?.jobTitle,
            specialtyArea: values?.specialtyArea,
            pharmacistsType: values?.pharmacistsType,
            isPatient: values?.isPatient,
            isCarer: values?.isCarer,
        });
    };

    return {
        getDefaultValues,
        professionsList: generateProfessionsSelectOptions(
            professionsDataWithoutPatientConsumer,
            [],
            t,
            variables.sortOrder.professionalSelectOptions,
            registrationData?.countryCode || accountData?.countryCode,
        ),
        specialtiesList: generateSpecialtiesList(),
        yearsList: generateYearsList(),
        medicalNumberConstraints: generateMedicalNumberConstraints(),
    };
};
